import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, type CreditPack, type Purchase } from '../lib/api';
import { errorMessage, usd, when } from '../lib/format';

const cents = (c: number) => `$${(c / 100).toFixed(2)}`;

/** Pack buttons: each opens a purchase and sends the browser to Checkout (Stripe, or the dev stand-in). */
export function BuyCredits() {
  const packs = useQuery({ queryKey: ['billing-packs'], queryFn: api.billingPacks });
  const [dollars, setDollars] = useState('');
  const checkout = useMutation({
    mutationFn: (choice: { packId: string } | { amountCents: number }) => api.checkout(choice),
    onSuccess: ({ url }) => {
      window.location.assign(url);
    },
  });
  if (!packs.data || packs.data.mode === 'off') return null;
  const { minCents, maxCents } = packs.data.custom;
  const amount = Number(dollars);
  const customOk =
    dollars.trim() !== '' &&
    Number.isInteger(amount) &&
    amount * 100 >= minCents &&
    amount * 100 <= maxCents;
  return (
    <div className="stack">
      <div className="row wrap">
        {packs.data.packs.map((p: CreditPack) => (
          <button
            key={p.id}
            type="button"
            disabled={checkout.isPending}
            onClick={() => checkout.mutate({ packId: p.id })}
            title={p.description}
          >
            {p.name} · {cents(p.priceCents)}
          </button>
        ))}
        {packs.data.mode !== 'live' && (
          <span className="pill" title="no real card is charged">
            {packs.data.mode === 'fake' ? 'dev checkout' : 'Stripe test mode'}
          </span>
        )}
      </div>
      <form
        className="row"
        onSubmit={(e) => {
          e.preventDefault();
          if (customOk) checkout.mutate({ amountCents: amount * 100 });
        }}
      >
        <label className="row" style={{ gap: 6 }}>
          <span className="muted">or any amount: $</span>
          <input
            type="number"
            inputMode="numeric"
            min={minCents / 100}
            max={maxCents / 100}
            step={1}
            placeholder={String(minCents / 100)}
            value={dollars}
            onChange={(e) => setDollars(e.target.value)}
            style={{ width: 110 }}
            aria-label="Custom amount in dollars"
          />
        </label>
        <button type="submit" disabled={!customOk || checkout.isPending}>
          Add {customOk ? cents(amount * 100) : 'credits'}
        </button>
        <span className="muted">
          whole dollars, {cents(minCents)} to {cents(maxCents)}
        </span>
      </form>
      {checkout.error && <p className="error">{errorMessage(checkout.error)}</p>}
    </div>
  );
}

/** Shown on return from Checkout; follows the purchase until the webhook marks it paid (or not). */
export function PurchaseOutcome({
  purchaseId,
  outcome,
  onSettled,
}: {
  purchaseId: string;
  outcome: string | undefined;
  onSettled: () => void;
}) {
  const purchase = useQuery({
    queryKey: ['purchase', purchaseId],
    queryFn: () => api.purchase(purchaseId),
    refetchInterval: (q) => (q.state.data?.status === 'pending' ? 2_000 : false),
  });
  const status = purchase.data?.status;
  useEffect(() => {
    if (status === 'paid') onSettled();
  }, [status, onSettled]);
  if (outcome === 'cancel')
    return <p className="muted">Checkout cancelled. Nothing was charged.</p>;
  if (purchase.error) return <p className="error">{errorMessage(purchase.error)}</p>;
  if (!purchase.data) return null;
  const p = purchase.data;
  if (p.status === 'paid')
    return (
      <p className="ok">
        Added {usd(p.creditsMicroUsd, 2)} of credits
        {p.packId === 'custom' ? '.' : ` (${p.packId} pack).`}
      </p>
    );
  if (p.status === 'pending')
    return <p className="muted">Payment received; waiting for Stripe to confirm it…</p>;
  return (
    <p className="error">
      Purchase {p.status}
      {p.note ? `: ${p.note}` : ''}. Nothing was credited.
    </p>
  );
}

export function PurchaseHistory() {
  const q = useQuery({ queryKey: ['purchases'], queryFn: api.purchases });
  const rows = q.data?.purchases ?? [];
  if (rows.length === 0) return null;
  return (
    <table>
      <thead>
        <tr>
          <th>When</th>
          <th>Pack</th>
          <th>Charged</th>
          <th>Credits</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>
        {rows.slice(0, 10).map((r: Purchase) => (
          <tr key={r.id}>
            <td>{when(r.createdAt)}</td>
            <td>{r.packId}</td>
            <td>{cents(r.priceCents)}</td>
            <td>{usd(r.creditsMicroUsd, 2)}</td>
            <td>{r.status}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/**
 * DEV ONLY stand-in for Stripe Checkout (fake gateway): Pay completes the purchase through the same
 * ledger path the webhook uses, Cancel goes back like Stripe's cancel_url would.
 */
export function DevCheckoutPage({
  purchaseId,
  success,
  cancel,
}: {
  purchaseId: string | undefined;
  success: string | undefined;
  cancel: string | undefined;
}) {
  const qc = useQueryClient();
  const pay = useMutation({
    mutationFn: () => api.devPay(purchaseId as string),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['credits'] });
      if (success) window.location.assign(success);
    },
  });
  if (!purchaseId) return <p className="error">Missing purchase.</p>;
  return (
    <div className="center">
      <div className="panel card stack">
        <h1 style={{ marginTop: 0 }}>Dev checkout</h1>
        <p className="muted">
          This deployment has no Stripe key. Pay credits the purchase exactly as a paid Stripe
          webhook would; no card is involved.
        </p>
        <div className="row">
          <button type="button" disabled={pay.isPending} onClick={() => pay.mutate()}>
            Pay
          </button>
          {cancel && (
            <button type="button" onClick={() => window.location.assign(cancel)}>
              Cancel
            </button>
          )}
        </div>
        {pay.error && <p className="error">{errorMessage(pay.error)}</p>}
      </div>
    </div>
  );
}
